import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import logo from './assets/logo_anduze.png';
import fondoVideo from './assets/login.mp4';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaSignInAlt } from 'react-icons/fa';
import { CircularProgress } from '@mui/material';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/ingresos-gastos');
    } catch {
      setError('Usuario o contraseña incorrectos');
      setShake(true);
      setTimeout(() => setShake(false), 600);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Video fondo */}
      <video
        autoPlay loop muted playsInline
        style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', objectFit: 'cover', zIndex: -2 }}
      >
        <source src={fondoVideo} type="video/mp4" />
      </video>

      {/* Overlay oscuro sobre el video */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: -1,
        background: 'linear-gradient(135deg, rgba(8,20,60,0.72) 0%, rgba(8,58,148,0.55) 100%)',
      }} />

      {/* Card centrada */}
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontFamily: 'Poppins, Segoe UI, sans-serif', padding: '1rem',
      }}>
        <motion.div
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, type: 'spring', bounce: 0.18 }}
          style={{
            width: '100%', maxWidth: 420,
            background: 'rgba(255,255,255,0.10)',
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            borderRadius: 28,
            border: '1.5px solid rgba(255,255,255,0.22)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
            padding: '2.8rem 2.4rem 2.2rem',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}
        >
          {/* Logo */}
          <motion.img
            src={logo}
            alt="Autocars Anduze"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            style={{
              width: 130,
              marginBottom: 20,
              filter: 'drop-shadow(0 4px 18px rgba(0,0,0,0.35))',
            }}
          />

          {/* Título */}
          <h2 style={{
            color: '#fff', fontWeight: 800, fontSize: 22,
            letterSpacing: '0.04em', margin: 0, textAlign: 'center',
          }}>
            AUTOCARS ANDUZE
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.65)', fontSize: 13,
            margin: '4px 0 28px', textAlign: 'center',
          }}>
            Sistema de gestión de taller
          </p>

          {/* Formulario */}
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>

            {/* Campo usuario */}
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <FaUser style={{
                position: 'absolute', top: '50%', left: 14,
                transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.5)', fontSize: 14,
              }} />
              <input
                type="text"
                placeholder="Usuario"
                value={username}
                autoFocus
                onChange={e => setUsername(e.target.value)}
                required
                style={inputStyle(!!error)}
              />
            </div>

            {/* Campo contraseña */}
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <FaLock style={{
                position: 'absolute', top: '50%', left: 14,
                transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.5)', fontSize: 14,
              }} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Contraseña"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{ ...inputStyle(!!error), paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: 'absolute', top: '50%', right: 12,
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.5)', fontSize: 15, padding: 4,
                  display: 'flex', alignItems: 'center',
                }}
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0, x: shake ? [0, -8, 8, -6, 6, 0] : 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    color: '#ff6b6b', fontSize: 13, textAlign: 'center',
                    marginBottom: 10, marginTop: 4,
                  }}
                >
                  ✕ {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Botón */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02, boxShadow: '0 8px 28px rgba(25,118,210,0.45)' }}
              whileTap={{ scale: 0.98 }}
              style={{
                width: '100%', marginTop: 10,
                padding: '12px 0', borderRadius: 14, border: 'none',
                background: loading
                  ? 'rgba(25,118,210,0.5)'
                  : 'linear-gradient(90deg, #1565c0 0%, #1e88e5 100%)',
                color: '#fff', fontWeight: 700, fontSize: 15,
                letterSpacing: '0.04em', cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background 0.2s',
                fontFamily: 'inherit',
              }}
            >
              {loading
                ? <CircularProgress size={16} color="inherit" />
                : <><FaSignInAlt /> Ingresar</>
              }
            </motion.button>
          </form>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        style={{
          position: 'fixed', bottom: 0, left: 0, width: '100%',
          textAlign: 'center', padding: '10px 0', zIndex: 99,
          fontSize: 12, color: 'rgba(255,255,255,0.55)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          background: 'rgba(0,0,0,0.15)',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        <a
          href="https://www.effsos.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'rgba(255,255,255,0.55)', textDecoration: 'none', transition: 'color 0.2s' }}
          onMouseOver={e => (e.target.style.color = '#5bc0f8')}
          onMouseOut={e => (e.target.style.color = 'rgba(255,255,255,0.55)')}
        >
          EFFSOS – Soluciones de Software Empresarial © {new Date().getFullYear()} · v0.10.4
        </a>
      </motion.footer>
    </>
  );
};

const inputStyle = (hasError) => ({
  width: '100%',
  padding: '11px 14px 11px 40px',
  borderRadius: 12,
  border: `1.5px solid ${hasError ? 'rgba(255,100,100,0.6)' : 'rgba(255,255,255,0.18)'}`,
  background: 'rgba(255,255,255,0.08)',
  color: '#fff',
  fontSize: 14,
  fontFamily: 'Poppins, sans-serif',
  outline: 'none',
  transition: 'border 0.2s, background 0.2s',
  boxSizing: 'border-box',
});

export default Login;
